import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    FileTransfersService,
    IOcbCUFileTransfer,
    IOcbEnvironmentAccess,
    IOcbFileTransfer,
    IOcbFileTransferInfo,
    IOcbLocalAgent,
    IOcbPgpKey,
    IOcbRemotePlatform,
    LocalAgentsService,
    OcbExecutionModes,
    PgpKeysService,
    RemotePlatformsService,
    TagsService,
    UserService
} from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxValidationGroupComponent } from 'devextreme-angular/ui/validation-group';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { Observable, forkJoin, from as fromPromise } from 'rxjs';
import { filter, finalize, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { FileTransferExecutionComponent } from '../../shared';

@Component({
    selector: 'app-file-transfers',
    templateUrl: './file-transfers.component.html',
    styleUrls: ['./file-transfers.component.scss'],
})
export class FileTransfersComponent implements AfterViewInit {

    @ViewChild('fileTransfers')
    public ftGrid: DxDataGridComponent;
    @ViewChild('fileTransferExecution')
    public ftExecution: FileTransferExecutionComponent;
    @ViewChild('fileTransferValidation')
    public ftValidationGroup: DxValidationGroupComponent;
    @ViewChild('copyValidation')
    public copyValidationGroup: DxValidationGroupComponent;

    // Main Data-Grid
    public dataSource: IOcbFileTransfer[] = null;
    public showDisabledTransfers: boolean = false;

    public currentEnvironmentDecryptionKeys: IOcbPgpKey[];
    public currentEnvironmentEncryptionKeys: IOcbPgpKey[];
    public currentEnvironmentFileTransfersInfo: IOcbFileTransferInfo[];
    public currentEnvironmentLocalAgents: IOcbLocalAgent[];
    public currentEnvironmentRemotePlatforms: IOcbRemotePlatform[];

    // Popup Display
    public isDisplayPopupVisible: boolean = false;
    public displayedFileTransfer: IOcbFileTransfer = null;
    public environmentsAccess: IOcbEnvironmentAccess[] = null;
    public filteredLocalAgents: IOcbLocalAgent[] = null;
    public filteredRemotePlatforms: IOcbRemotePlatform[] = null;

    // Popup New & Edit
    public isEditPopupVisible: boolean = false;
    public selectedFileTransfer: IOcbFileTransfer = null;
    public isNew: boolean = null;

    // References
    public decryptionKeys: IOcbPgpKey[] = null;
    public encryptionKeys: IOcbPgpKey[] = null;
    public localAgents: IOcbLocalAgent[] = null;
    public remotePlatforms: IOcbRemotePlatform[] = null;
    public executionModes: any[] = new Array<any>();
    public isLoadPanelVisible: boolean = false;
    public tags: string[] = new Array<string>();

    // Regex
    public regexSource = new RegExp(/\\\\[a-zA-Z0-9\.\-_]{1,}.*/g);
    public regexTarget = new RegExp(/\\\\[a-zA-Z0-9\.\-_]{1,}.*/g);
    public regexAck = new RegExp(/\\\\[a-zA-Z0-9\.\-_]{1,}.*/g);
    public regexArchives = new RegExp(/\\\\[a-zA-Z0-9\.\-_]{1,}.*/g);

    private currentEnvironmentId: string = '';
    private executionModesLabels: any;
    
    constructor(
        private route: ActivatedRoute,
        private fileTransfersService: FileTransfersService,
        private userService: UserService,
        private localAgentsService: LocalAgentsService,
        private remotePlatformsService: RemotePlatformsService,
        private translateService: TranslateService,
        private tagsService: TagsService,
        private pgpKeysService: PgpKeysService,
        private router: Router
    ) {
        this.displayExecutionMode = this.displayExecutionMode.bind(this);
    }
    
    public onContextMenuPreparing(e) {
        if (e.row && e.row.rowType === 'data') {
            forkJoin([
                this.translateService.get('_commons.seeDetails'),
                this.translateService.get('_commons.edit'),
                this.translateService.get('_commons.copy'),
                this.translateService.get('_commons.renamingParameters'),
                this.translateService.get('_commons.alertsRecipients'),
                this.translateService.get('_commons.apiKeys'),
                this.translateService.get('_commons.logs'),
                this.translateService.get('_commons.execute'),
                this.translateService.get('_commons.enable'),
                this.translateService.get('_commons.disable'),
                this.translateService.get('_commons.delete'),
                this.translateService.get('_commons.editWithOldForm'),
                this.translateService.get('_commons.copyWithOldForm')
            ]).subscribe({
                next: (messages) => {
                    if (e.row.data.isDisabled) {
                        e.items = [
                            { text: messages[0], icon: 'description', onItemClick: (ev) => this.displayFileTransfer(e.row) },
                            { text: messages[1], icon: 'edit', onItemClick: (ev) => this.router.navigateByUrl(`/${this.currentEnvironmentId}/file-transfers/${e.row.data.id}`) },
                            { text: messages[11], icon: 'edit', onItemClick: (ev) => this.displayUpdatePopup(ev, e.row.data) },
                            { text: messages[2], icon: 'copy', onItemClick: (ev) => this.copyFileTransfer(e.row.data.id, true) },
                            { text: messages[12], icon: 'copy', onItemClick: (ev) => this.copyFileTransfer(e.row.data.id, false) },
                            { text: messages[8], icon: 'check', onItemClick: (ev) => this.enableFileTransfer(ev, e.row.data.id) },
                            { text: messages[3], icon: 'variable', beginGroup: true, onItemClick: (ev) => this.router.navigateByUrl(`/${this.currentEnvironmentId}/file-transfers-renaming-parameters?fileTransfer=${e.row.data.id}`) },
                            { text: messages[4], icon: 'message', onItemClick: (ev) => this.router.navigateByUrl(`/${this.currentEnvironmentId}/file-transfers-alerts?fileTransfer=${e.row.data.id}`) },
                            { text: messages[5], icon: 'lock', onItemClick: (ev) => this.router.navigateByUrl(`/${this.currentEnvironmentId}/file-transfers-api-keys?fileTransfer=${e.row.data.id}`) },
                            { text: messages[6], icon: 'clock', beginGroup: true, onItemClick: (ev) => this.router.navigateByUrl(`/${this.currentEnvironmentId}/logs?fileTransfer=${e.row.data.id}`) },
                            { text: messages[10], icon: 'trash', beginGroup: true, onItemClick: (ev) => this.deleteFileTransfer(ev, e.row.data.id) }
                        ];
                    }
                    else {
                        e.items = [
                            { text: messages[0], icon: 'description', onItemClick: (ev) => this.displayFileTransfer(e.row) },
                            { text: messages[1], icon: 'edit', onItemClick: (ev) => this.router.navigateByUrl(`/${this.currentEnvironmentId}/file-transfers/${e.row.data.id}`) },
                            { text: messages[11], icon: 'edit', onItemClick: (ev) => this.displayUpdatePopup(ev, e.row.data) },
                            { text: messages[2], icon: 'copy', onItemClick: (ev) => this.copyFileTransfer(e.row.data.id, true) },
                            { text: messages[12], icon: 'copy', onItemClick: (ev) => this.copyFileTransfer(e.row.data.id, false) },
                            { text: messages[3], icon: 'variable', beginGroup: true, onItemClick: (ev) => this.router.navigateByUrl(`/${this.currentEnvironmentId}/file-transfers-renaming-parameters?fileTransfer=${e.row.data.id}`) },
                            { text: messages[4], icon: 'message', onItemClick: (ev) => this.router.navigateByUrl(`/${this.currentEnvironmentId}/file-transfers-alerts?fileTransfer=${e.row.data.id}`) },
                            { text: messages[5], icon: 'lock', onItemClick: (ev) => this.router.navigateByUrl(`/${this.currentEnvironmentId}/file-transfers-api-keys?fileTransfer=${e.row.data.id}`) },
                            { text: messages[6], icon: 'clock', beginGroup: true, onItemClick: (ev) => this.router.navigateByUrl(`/${this.currentEnvironmentId}/logs?fileTransfer=${e.row.data.id}`) },
                            { text: messages[7], icon: 'video', beginGroup: true, onItemClick: (ev) => { this.ftExecution.fileTransferId = e.row.data.id; this.ftExecution.visible = true; } },
                            { text: messages[9], icon: 'clear', beginGroup: true, onItemClick: (ev) => this.disableFileTransfer(ev, e.row.data.id) },
                            { text: messages[10], icon: 'trash', onItemClick: (ev) => this.deleteFileTransfer(ev, e.row.data.id) }
                        ];
                    }
                },
                error: (err) => this.handleError(err)
            });
        }
    }

    public ngAfterViewInit() {
        this.route.params.subscribe(_ => {
            this.currentEnvironmentId = this.route.snapshot.params.id;
            this.translateService.get('executionModes').subscribe(labels => {
                this.executionModesLabels = labels;
                this.loadData();
            });
        });
    }

    public onExtractZipArchiveInTargetFolder(e) {
        if (!this.selectedFileTransfer) return;
        if (e.value) {
            this.selectedFileTransfer.overwriteFileInTargetFolder = true;
            this.selectedFileTransfer.replaceUnixLineFeedCharacter = false;
        }
    }

    public onIgnoreEmptyFilesChanged(e) {
        if (!this.selectedFileTransfer) return;
        if (!e.value) {
            this.selectedFileTransfer.createLogForIgnoredEmptyFiles = false;
            this.selectedFileTransfer.removeEmptyFiles = false;
        }
    }

    public openNewCreationForm() {
        this.router.navigateByUrl(`/${this.currentEnvironmentId}/file-transfers/new`);
    }

    public onRowDblClick(e) {
        if (e?.data) {
            this.router.navigateByUrl(`/${this.currentEnvironmentId}/file-transfers/${e.data.id}`)
        }
    }

    public onRowPrepared(e): void {
        if (this.showDisabledTransfers === true) {
            if (e.data != null) {
                if (e.data.isDisabled === true) { e.rowElement.bgColor = '#FFCECE'; }
            }
        }
    }

    public displayNewPopup(): void {
        this.selectedFileTransfer = this.getNewIOcbFileTransfer();
        this.isNew = true;
        this.isEditPopupVisible = true;
    }

    public displayUpdatePopup(e, fileTransfer: IOcbFileTransfer): void {
        if (e) e.event.stopPropagation();
        this.selectedFileTransfer = this.getIOcbFileTransferFromData(fileTransfer);
        this.isNew = false;
        this.isEditPopupVisible = true;
    }
    
    public createFileTransfer(): void {
        if (this.ftValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            const newFileTransfer = this.getIOcbCUFileTransferFromData(this.selectedFileTransfer);
            this.fileTransfersService.addFileTransfer(newFileTransfer)
                .pipe(
                    mergeMap(_ => this.translateService.get('fileTransfers.msgFileTransferCreationComplete')),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe({
                    next: (message) => {
                        this.isEditPopupVisible = false;
                        this.loadDataSource(this.showDisabledTransfers);
                        this.loadFileTransfersPicker();
                        this.loadTags();
                        notify(message, 'success', 3000);
                    },
                    error: (err) => this.handleError(err)
                });
        }
    }

    public updateFileTransfer(): void {
        if (this.ftValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            const updateFileTransfer = this.getIOcbCUFileTransferFromData(this.selectedFileTransfer);
            this.fileTransfersService.updateFileTransfer(this.selectedFileTransfer.id, updateFileTransfer)
                .pipe(
                    mergeMap(_ => this.translateService.get('fileTransfers.msgFileTransferUpdateComplete')),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe({
                    next: (message) => {
                        this.isEditPopupVisible = false;
                        this.loadDataSource(this.showDisabledTransfers);
                        this.loadFileTransfersPicker();
                        this.loadTags();
                        notify(message, 'success', 3000);
                    },
                    error: (err) => this.handleError(err)
                });
        }
    }

    public deleteFileTransfer(e, id): void {
        e.event.stopPropagation();
        forkJoin([
            this.translateService.get('fileTransfers.msgDeleteConfirm'),
            this.translateService.get('fileTransfers.msgDeleteConfirmTitle')
        ])
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the RESET request
                mergeMap(_ => this.fileTransfersService.deleteFileTransfer(id)),
                // Reload the email template
                mergeMap(_ => this.translateService.get('fileTransfers.msgFileTransferDeleteComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (message) => {
                    this.loadDataSource(this.showDisabledTransfers);
                    this.loadFileTransfersPicker();
                    this.loadTags();
                    notify(message, 'success', 3000);
                },
                error: (err) => {
                    if (err.status === 409) {
                        this.disableFileTransfer(null, id);
                    }
                    this.handleError(err);
                }
            });
    }

    public enableFileTransfer(e, id): void {
        e.event.stopPropagation();
        this.isLoadPanelVisible = true;
        this.fileTransfersService.enableFileTransfer(id)
            .pipe(
                mergeMap(_ => this.translateService.get('fileTransfers.msgFileTransferEnableComplete')),
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (message) => {
                    this.isLoadPanelVisible = false;
                    this.loadDataSource(this.showDisabledTransfers);
                    this.loadFileTransfersPicker();
                    notify(message, 'success', 3000);
                },
                error: (err) => this.handleError(err)
            });
    }

    public disableFileTransfer(e, id): void {
        if (e !== null) {
            e.event.stopPropagation();
        }
        this.isLoadPanelVisible = true;
        this.fileTransfersService.disableFileTransfer(id)
            .pipe(
                mergeMap(_ => this.translateService.get('fileTransfers.msgFileTransferDisableComplete')),
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (message) => {
                    this.loadDataSource(this.showDisabledTransfers);
                    this.loadFileTransfersPicker();
                    notify(message, 'success', 3000);
                },
                error: (err) => this.handleError(err)
            });
    }

    public displayFileTransfer(e): void {
        this.displayedFileTransfer = e.data;
        if (!this.displayedFileTransfer.source) {
            this.displayedFileTransfer.source = this.emptyPlatform();
        }
        if (!this.displayedFileTransfer.target) {
            this.displayedFileTransfer.target = this.emptyPlatform();
        }
        if (!this.displayedFileTransfer.archives) {
            this.displayedFileTransfer.archives = this.emptyPlatform();
        }
        this.isDisplayPopupVisible = true;
    }

    public copyFileTransfer(id: string, redirectToNewForm: boolean) {
        forkJoin([
            this.translateService.get('fileTransfers.msgCopyConfirm'),
            this.translateService.get('fileTransfers.msgCopyConfirmTitle')
        ]).subscribe({
            next: (messages) => {
                confirm(messages[0], messages[1]).then((confirmed) => {
                    if (!confirmed) return;
                    this.isLoadPanelVisible = true;
                    this.fileTransfersService.copyFileTransfer(id).subscribe({
                        next: (fileTransfer) => {
                            if (!redirectToNewForm) {
                                this.displayUpdatePopup(null, fileTransfer);
                                this.loadDataSource(this.showDisabledTransfers);
                            }
                            else {
                                this.router.navigateByUrl(`/${this.currentEnvironmentId}/file-transfers/${fileTransfer.id}`);
                            }
                        },
                        error: (error) => this.handleError(error),
                        complete: () => this.isLoadPanelVisible = false
                    });
                });
            },
            error: (err: any) => this.handleError(err)
        });
    }

    public filterChanged(showDisabledTransfers: boolean) {
        this.showDisabledTransfers = showDisabledTransfers;
        if (this.showDisabledTransfers) { this.loadDataSource(true); }
        else { this.loadDataSource(false); }
    }

    public displayExecutionMode(rowData) {
        return this.executionModesLabels[OcbExecutionModes[rowData.executionMode]];
    }

    public notifyCopy(): void {
        this.translateService.get('_clipboard.copied')
            .subscribe(message => {
                notify(message, 'success', 3000);
            });
    }

    private loadData(): void {
        this.loadDataSource(false);

        this.userService.user.pipe(
            take(1),
            map(user => user.environments.filter(env => env.canManageFileTransfers)),
        ).subscribe(envs => this.environmentsAccess = envs);

        this.userService.user.pipe(
            take(1),
            // Get all local agents from service
            switchMap(_ => this.localAgentsService.getLocalAgents()),
            // For each agent, check permissions
            mergeMap(agents => this.checkLocalAgentsPermissions(agents)),
            // Filter agents with no permissions
            map(agents => agents.filter(ag => ag !== null)),
        ).subscribe(agents => {
            this.localAgents = agents;
        });

        this.localAgentsService.getLocalAgents(this.currentEnvironmentId, false).subscribe(localAgents => {
            this.currentEnvironmentLocalAgents = localAgents;
        });

        this.pgpKeysService.getPgpKeys(this.currentEnvironmentId, 'decryption').subscribe(keys => {
            this.currentEnvironmentDecryptionKeys = keys;
        });

        this.pgpKeysService.getPgpKeys(this.currentEnvironmentId, 'encryption').subscribe(keys => {
            this.currentEnvironmentEncryptionKeys = keys;
        });

        this.remotePlatformsService.getRemotePlatforms(this.currentEnvironmentId, null, false).subscribe(remotePlatforms => {
            this.currentEnvironmentRemotePlatforms = remotePlatforms;
        });

        this.loadFileTransfersPicker();
        this.loadTags();

        this.translateService.get('executionModes').subscribe(labels => {
            for (const em in OcbExecutionModes) {
                if (isNaN(Number(em))) {
                    this.executionModes.push({ name: labels[em], id: OcbExecutionModes[em] });
                }
            }
        });
    }

    private loadDataSource(includeDisabled: boolean): void {
        this.showLoading$('_commons.loading')
            .pipe(
                mergeMap(_ => this.fileTransfersService.getFileTransfers(this.currentEnvironmentId, includeDisabled)),
                finalize(() => this.ftGrid.instance.endCustomLoading()))
            .subscribe({
                next: (res) => this.dataSource = res,
                error: (err) => this.handleError(err)
            });
    }

    private loadFileTransfersPicker(): void {
        this.fileTransfersService.getFileTransfersInfo(this.currentEnvironmentId).subscribe(fileTransfersInfo => {
            this.currentEnvironmentFileTransfersInfo = fileTransfersInfo;
        });
    }

    private loadTags(): void {
        this.tagsService.getTags(this.currentEnvironmentId).subscribe(tags => { this.tags = tags.map(t => t.value) });
    }

    private getNewIOcbFileTransfer(): IOcbFileTransfer {
        return {
            id: '',
            environmentId: '',
            name: '',
            localAgent: null,
            fileNamePattern: '',
            deleteTransferredFileFromSource: false,
            ackManagement: false,
            ackokExtension: '',
            ackkoExtension: '',
            renamingParametersSeparator: '',
            renameOnlyArchivedFiles: false,
            replaceSpecialCharactersInFileName: false,
            overwriteFileInTargetFolder: false,
            sendFileInNotificationEmail: false,
            source: null,
            target: null,
            decryptionKey: null,
            encryptionKey: null,
            archives: null,
            sourceFolder: '',
            targetFolder: '',
            ackFolder: '',
            archivesFolder: '',
            encryptArchivedFile: false,
            executionMode: null,
            executeAfter: null,
            isDisabled: false,
            ignoreEmptyFiles: false,
            createLogForIgnoredEmptyFiles: false,
            removeEmptyFiles: false,
            extractZipArchiveInTargetFolder: false,
            replaceUnixLineFeedCharacter: false,
            sourceTags: new Array<string>(),
            targetTags: new Array<string>(),
        };
    }

    private getIOcbFileTransferFromData(data: IOcbFileTransfer): IOcbFileTransfer {
        return {
            id: data.id,
            environmentId: data.environmentId,
            name: data.name,
            localAgent: data.localAgent,
            fileNamePattern: data.fileNamePattern,
            deleteTransferredFileFromSource: data.deleteTransferredFileFromSource,
            ackManagement: data.ackManagement,
            ackokExtension: data.ackokExtension,
            ackkoExtension: data.ackkoExtension,
            renamingParametersSeparator: data.renamingParametersSeparator,
            renameOnlyArchivedFiles: data.renameOnlyArchivedFiles,
            replaceSpecialCharactersInFileName: data.replaceSpecialCharactersInFileName,
            overwriteFileInTargetFolder: data.overwriteFileInTargetFolder,
            sendFileInNotificationEmail: data.sendFileInNotificationEmail,
            source: data.source,
            target: data.target,
            decryptionKey: data.decryptionKey,
            encryptionKey: data.encryptionKey,
            archives: data.archives,
            sourceFolder: data.sourceFolder,
            targetFolder: data.targetFolder,
            ackFolder: data.ackFolder,
            archivesFolder: data.archivesFolder,
            encryptArchivedFile: data.encryptArchivedFile,
            executionMode: data.executionMode,
            executeAfter: data.executeAfter,
            isDisabled: data.isDisabled,
            ignoreEmptyFiles: data.ignoreEmptyFiles,
            createLogForIgnoredEmptyFiles: data.createLogForIgnoredEmptyFiles,
            removeEmptyFiles: data.removeEmptyFiles,
            extractZipArchiveInTargetFolder: data.extractZipArchiveInTargetFolder,
            replaceUnixLineFeedCharacter: data.replaceUnixLineFeedCharacter,
            sourceTags: data.sourceTags,
            targetTags: data.targetTags,
        };
    }

    private getIOcbCUFileTransferFromData(data: IOcbFileTransfer): IOcbCUFileTransfer {
        return {
            environmentId: data.environmentId ? data.environmentId : this.currentEnvironmentId,
            name: data.name,
            localAgentId: data.localAgent.id,
            fileNamePattern: data.fileNamePattern,
            deleteTransferredFileFromSource: data.deleteTransferredFileFromSource,
            ackManagement: data.ackManagement,
            ackokExtension: data.ackokExtension,
            ackkoExtension: data.ackkoExtension,
            renamingParametersSeparator: data.renamingParametersSeparator,
            renameOnlyArchivedFiles: data.renameOnlyArchivedFiles,
            replaceSpecialCharactersInFileName: data.replaceSpecialCharactersInFileName,
            overwriteFileInTargetFolder: data.overwriteFileInTargetFolder,
            sendFileInNotificationEmail: data.sendFileInNotificationEmail,
            sourceId: data.source ? data.source.id : null,
            targetId: data.target ? data.target.id : null,
            decryptionKeyId: data.decryptionKey ? data.decryptionKey.id : null,
            encryptionKeyId: data.encryptionKey ? data.encryptionKey.id : null,
            archivesId: data.archives ? data.archives.id : null,
            sourceFolder: data.sourceFolder,
            targetFolder: data.targetFolder,
            ackFolder: data.ackFolder,
            archivesFolder: data.archivesFolder,
            encryptArchivedFile: data.encryptArchivedFile,
            executionMode: data.executionMode,
            executeOnlyAfterId: data.executeAfter?.id ? data.executeAfter?.id : null,
            ignoreEmptyFiles: data.ignoreEmptyFiles,
            createLogForIgnoredEmptyFiles: data.createLogForIgnoredEmptyFiles,
            removeEmptyFiles: data.removeEmptyFiles,
            extractZipArchiveInTargetFolder: data.extractZipArchiveInTargetFolder,
            replaceUnixLineFeedCharacter: data.replaceUnixLineFeedCharacter,
            sourceTags: data.sourceTags,
            targetTags: data.targetTags,
        };
    }

    private checkLocalAgentsPermissions(agents: IOcbLocalAgent[]): Observable<IOcbLocalAgent[]> {
        return forkJoin(
            // For each agent...
            agents.map(agent => {
                // Return agent if user has permission or return null
                return this.userService.hasEnvironmentPermission(agent.environmentId, 'canManageFileTransfers').pipe(
                    take(1),
                    map(bool => bool ? agent : null),
                );
            }),
        );
    }

    private emptyPlatform(): IOcbRemotePlatform {
        return {
            environmentId: '',
            name: '',
            displayName: '',
            ipFiltering: false,
            isDisabled: false
        };
    }

    private showLoading$(messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => this.ftGrid.instance.beginCustomLoading(`${message}...`)),
        );
    }

    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            } else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            } else {
                message = error.error;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }
}
